/**
 * Web-only draft chat surface.
 *
 * This follows T3 Code's DraftHeroHeadline + ComposerSurface structure directly:
 * the destination is part of the sentence, the project menu is inline, and the
 * prompt is a floating composer rather than a settings form. Comet's existing
 * store remains the adapter at the edge: a submit first mints the required
 * Space-backed chat and then sends the prompt through the existing RPC.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useAppState, useStore } from "./store-context";
import { IconArrowUp, IconChevron, IconDevice, IconPlus } from "./icons";
import { BranchToolbar } from "./branch-toolbar";
import { groupSpacesByRepository, spaceLabel } from "../lib/models";
import type { RepositoryGroup, SandboxLevel, Space } from "../lib/models";

const SANDBOX_LABELS: Record<SandboxLevel, string> = {
  readOnly: "Read only",
  workspaceWrite: "Can edit",
  dangerFullAccess: "Full access"
};

export function NewChatScreen({
  onCreated,
  onCancel
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const state = useAppState();
  const store = useStore();
  const draft = state.newChat;
  const [prompt, setPrompt] = useState("");
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [sandboxMenuOpen, setSandboxMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const space = state.spaces.find((candidate) => candidate.id === draft?.spaceId);
  const repositories = groupSpacesByRepository(state.spaces);

  useEffect(() => {
    if (space !== undefined) void store.loadRefs(space);
  }, [space, store]);

  useEffect(() => {
    setProjectMenuOpen(false);
  }, [draft?.spaceId]);

  if (draft === undefined) return null;

  const projectTitle = space === undefined ? null : spaceLabel(space);
  const hasProjects = repositories.length > 0;
  const canSubmit = prompt.trim().length > 0 && !submitting;
  const currentModel = draft.model ?? draft.harness ?? "Model";
  const currentSandbox = draft.sandbox ?? "workspaceWrite";

  const submit = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const chatId = await store.createChatFromDraft();
      if (chatId === undefined) return;
      // createChatFromDraft opens the newly-created chat while preserving the
      // existing backend contract. The first prompt is then sent through the
      // same path as every established conversation.
      store.setDraft({ text: prompt });
      await store.send();
      onCreated();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-0 flex-1 overflow-hidden bg-background" data-chat-view="true">
      <div className="flex min-h-full w-full flex-col justify-center overflow-y-auto px-4 py-12 sm:px-6">
        <div className="mx-auto flex w-full max-w-[var(--chat-content-max-width)] flex-col items-center">
          <DraftHeroHeadline
            projectTitle={projectTitle}
            project={space}
            hasProjects={hasProjects}
            repositories={repositories}
            devices={state.devices}
            projectMenuOpen={projectMenuOpen}
            onProjectMenuOpenChange={setProjectMenuOpen}
            onSelectProject={(spaceId) => {
              store.setNewChat({ spaceId });
              setProjectMenuOpen(false);
            }}
            onCancelProject={() => {
              store.setNewChat({ spaceId: undefined });
              setProjectMenuOpen(false);
            }}
          />

          <form className="mt-8 w-full" onSubmit={(event) => void submit(event)}>
            <div data-slot="composer-shell" className="@container/composer-surface group/composer-surface relative isolate mx-auto w-full max-w-(--chat-content-max-width) rounded-3xl p-px shadow-[var(--shadow-composer)] before:pointer-events-none before:absolute before:inset-0 before:rounded-3xl before:bg-card/80 before:backdrop-blur-xl after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-[var(--chat-composer-outline)]">
              <div data-slot="composer-host" className="relative rounded-[inherit] bg-card px-3 pt-3.5 pb-2 sm:px-4 sm:pt-4">
                <textarea
                  data-chat-composer-body="true"
                  autoFocus
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                      event.preventDefault();
                      void submit();
                    }
                  }}
                  rows={3}
                  placeholder="What would you like to build?"
                  aria-label="New chat prompt"
                  className="block min-h-24 w-full resize-none bg-transparent text-base leading-relaxed text-foreground outline-none placeholder:text-placeholder"
                />
                <div className="mt-3 flex min-w-0 items-center gap-1.5">
                  <div className="relative min-w-0">
                    <button
                      type="button"
                      onClick={() => {
                        setModelMenuOpen((open) => !open);
                        setSandboxMenuOpen(false);
                      }}
                      className="flex h-8 max-w-[11rem] items-center gap-1 rounded-full border border-transparent px-2.5 text-xs text-muted-foreground hover:bg-accent"
                    >
                      <span className="truncate">{currentModel}</span>
                      <IconChevron className="size-3.5 shrink-0 opacity-60" />
                    </button>
                    {modelMenuOpen ? (
                      <Menu className="bottom-10 left-0 w-60">
                        <MenuLabel>Agent and model</MenuLabel>
                        {state.harnesses.map((harness) => (
                          <MenuItem
                            key={harness.id}
                            active={draft.harness === harness.id}
                            onClick={() => {
                              store.setNewChat({ harness: harness.id, model: null });
                              void store.loadModelsForNewChat(harness.id);
                              setModelMenuOpen(false);
                            }}
                          >
                            {harness.name ?? harness.id}
                          </MenuItem>
                        ))}
                        {state.models.map((model) => (
                          <MenuItem
                            key={model.id}
                            active={draft.model === model.id}
                            onClick={() => {
                              store.setNewChat({ model: model.id });
                              setModelMenuOpen(false);
                            }}
                          >
                            <span className="truncate">{model.label ?? model.id}</span>
                          </MenuItem>
                        ))}
                        {state.harnesses.length === 0 && state.models.length === 0 ? (
                          <p className="px-2 py-2 text-xs text-muted-foreground">No models listed.</p>
                        ) : null}
                      </Menu>
                    ) : null}
                  </div>

                  <div className="relative min-w-0">
                    <button
                      type="button"
                      onClick={() => {
                        setSandboxMenuOpen((open) => !open);
                        setModelMenuOpen(false);
                      }}
                      className="flex h-8 items-center gap-1 rounded-full border border-transparent px-2.5 text-xs text-muted-foreground hover:bg-accent"
                    >
                      {SANDBOX_LABELS[currentSandbox]}
                      <IconChevron className="size-3.5 shrink-0 opacity-60" />
                    </button>
                    {sandboxMenuOpen ? (
                      <Menu className="bottom-10 left-0 w-48">
                        {Object.entries(SANDBOX_LABELS).map(([level, label]) => (
                          <MenuItem
                            key={level}
                            active={currentSandbox === level}
                            onClick={() => {
                              store.setNewChat({ sandbox: level as SandboxLevel });
                              setSandboxMenuOpen(false);
                            }}
                          >
                            {label}
                          </MenuItem>
                        ))}
                      </Menu>
                    ) : null}
                  </div>

                  <div className="flex-1" />
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    aria-label="Start chat"
                    title="Start chat"
                    className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity disabled:bg-muted disabled:text-muted-foreground"
                  >
                    <IconArrowUp className="size-4" />
                  </button>
                </div>
              </div>
            </div>

            {space !== undefined ? (
              <BranchToolbar
                space={space}
                spaces={state.spaces}
                devices={state.devices}
                refs={state.refs ?? []}
                worktree={draft.worktree}
                baseRef={draft.baseRef}
                onSpaceChange={(spaceId) => store.setNewChat({ spaceId })}
                onWorktreeChange={(worktree) => store.setNewChat({ worktree })}
                onBaseRefChange={(baseRef) => store.setNewChat({ baseRef })}
              />
            ) : null}
          </form>

          {state.error !== undefined ? (
            <p className="mt-3 text-sm text-error-foreground">{state.error}</p>
          ) : null}

          <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
            <IconDevice className="size-3.5" />
            Runs on {space === undefined ? deviceLabel(state.deviceId, state.devices) : deviceLabel(space.deviceId, state.devices)}
          </div>

          <button
            type="button"
            onClick={() => {
              store.closeNewChat();
              onCancel();
            }}
            className="mt-8 rounded-full px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function DraftHeroHeadline({
  projectTitle,
  project,
  hasProjects,
  repositories,
  devices,
  projectMenuOpen,
  onProjectMenuOpenChange,
  onSelectProject,
  onCancelProject
}: {
  projectTitle: string | null;
  project: Space | undefined;
  hasProjects: boolean;
  repositories: readonly RepositoryGroup[];
  devices: readonly { id: string; name?: string | null }[];
  projectMenuOpen: boolean;
  onProjectMenuOpenChange: (open: boolean) => void;
  onSelectProject: (spaceId: string) => void;
  onCancelProject: () => void;
}) {
  const projectSelector = hasProjects ? (
    <div className="relative inline-block max-w-full align-baseline">
      <button
        type="button"
        onClick={() => onProjectMenuOpenChange(!projectMenuOpen)}
        aria-expanded={projectMenuOpen}
        className="inline-flex max-w-full cursor-pointer items-center rounded-md border-b border-dotted border-muted-foreground/50 px-1 text-foreground transition-colors hover:border-muted-foreground hover:bg-accent"
      >
        <span className="truncate">{projectTitle ?? "Choose a project"}</span>
      </button>
      {projectMenuOpen ? (
        <div className="absolute top-[calc(100%+0.5rem)] left-1/2 z-30 max-h-80 w-72 -translate-x-1/2 overflow-y-auto rounded-xl border border-border bg-popover p-1 text-left shadow-lg">
          {repositories.map((repository) => (
            <div key={repository.key} className="px-1 py-1">
              <div className="px-2 py-1 text-2xs font-medium tracking-wide text-muted-foreground uppercase">
                {repository.label}
              </div>
              <div className="flex flex-col gap-0.5">
                {repository.spaces.map((candidate) => (
                  <button
                    key={candidate.id}
                    type="button"
                    onClick={() => onSelectProject(candidate.id)}
                    className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm hover:bg-accent ${candidate.id === project?.id ? "bg-accent" : ""}`}
                  >
                    <IconDevice className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">{deviceLabel(candidate.deviceId, devices)}</span>
                    <span className="max-w-32 truncate text-xs text-muted-foreground/70">
                      {candidate.path}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            onClick={onCancelProject}
            className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-muted-foreground hover:bg-accent"
          >
            <IconPlus className="size-4" />
            No project
          </button>
        </div>
      ) : null}
    </div>
  ) : (
    <span className="text-muted-foreground">Add a project</span>
  );

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col items-center">
      <h1 className="w-full text-center text-2xl font-normal tracking-tight text-foreground sm:text-3xl">
        {project === undefined ? <>What should we work on?</> : <>What should we build in {projectSelector}?</>}
      </h1>
      <p className="mt-2 flex h-6 items-center text-sm">
        {project === undefined && hasProjects ? (
          projectSelector
        ) : project !== undefined && hasProjects ? (
          <button
            type="button"
            onClick={onCancelProject}
            className="pointer-events-auto rounded-md px-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            or start without a project
          </button>
        ) : null}
      </p>
    </div>
  );
}

function Menu({ className, children }: { className: string; children: ReactNode }) {
  return (
    <div className={`absolute z-30 max-h-72 overflow-y-auto rounded-xl border border-border bg-popover p-1 shadow-lg ${className}`}>
      {children}
    </div>
  );
}

function MenuLabel({ children }: { children: ReactNode }) {
  return <p className="px-2 py-1.5 text-2xs font-medium text-muted-foreground">{children}</p>;
}

function MenuItem({
  children,
  active,
  onClick
}: {
  children: ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center rounded-lg px-2 py-1.5 text-left text-xs hover:bg-accent ${active ? "bg-accent text-foreground" : "text-muted-foreground"}`}
    >
      {children}
    </button>
  );
}

const deviceLabel = (
  id: string | undefined,
  devices: readonly { id: string; name?: string | null }[]
): string => {
  const device = devices.find((candidate) => candidate.id === id);
  return device?.name ?? device?.id ?? "this device";
};
